import { useEffect, useRef, useState } from "react";
import { createBrowserRouter, RouterProvider, useLocation } from "react-router-dom";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import { App } from "@/App";
import { Loading } from "@/components/Loading";
import routes from "@/routes";

import {
  handleClientApplicationRestriction,
  isClientApplicationAllowedRoute,
  isExpiredSession,
  isPublicAuthPathname,
  isRestrictedClientApplication,
  setNextRedirectUrl,
} from "./utils/medplum";

// TODO(refactor): Consolidate session gating in AppContent — the early `!ready` branch
// (expired/restricted + ref) overlaps with the post-ready `isRestrictedClientApplication` /
// `isExpiredSession` checks. Consider a single `resolveSessionGate(...)` helper that returns
// loading | redirect | render-app, preserving `effectiveProfile` vs `profile` semantics.

// Component that handles session checks and is rendered inside router context
function AppContent(): JSX.Element {
  const { pathname } = useLocation();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const shouldBypassLoadingGate = isPublicAuthPathname(pathname);
  const [ready, setReady] = useState(shouldBypassLoadingGate);
  const isAllowedRoute = isClientApplicationAllowedRoute(pathname);
  const hasHandledExpiredOrRestrictedRef = useRef(false);

  const effectiveProfile = (profile ?? medplum.getProfile()) as
    | { resourceType?: string }
    | undefined;
  const isRestricted = isRestrictedClientApplication(effectiveProfile);

  useEffect(() => {
    if (shouldBypassLoadingGate) {
      setReady(true);
      return;
    }
    if (!medplum.isLoading()) {
      setReady(true);
    }
  }, [medplum, profile, pathname, shouldBypassLoadingGate]);

  if (!ready) {
    // Only redirect early if there is definitively no active login at all.
    // getProfile() can be null during normal initialization even for valid sessions,
    // so we must not use isExpiredSession() here — it would incorrectly redirect
    // authenticated users while Medplum is still restoring state from storage.
    // The post-ready isExpiredSession() check below handles the expired-token case.
    if (
      !hasHandledExpiredOrRestrictedRef.current &&
      !isAllowedRoute &&
      (isRestricted || !medplum.getActiveLogin())
    ) {
      hasHandledExpiredOrRestrictedRef.current = true;
      if (isRestricted) {
        handleClientApplicationRestriction(medplum);
      } else {
        setNextRedirectUrl();
      }
      return <Loading />;
    }

    return <Loading />;
  }

  // ClientApplication users should not access the main application
  // Redirect them to the sign-in page to prevent unauthorized access
  if (isRestrictedClientApplication(profile) && !isAllowedRoute) {
    handleClientApplicationRestriction(medplum);
    return <Loading />;
  }

  if (isExpiredSession(medplum) && !isAllowedRoute) {
    setNextRedirectUrl();
    return <Loading />;
  }

  return <App />;
}

// TODO: If both App.tsx and AppContent loading bypasses ever feel redundant, validate
// that removing one layer does not reintroduce endless spinners on `/` or OAuth after
// token expiry (they guard different layers: shell vs router `ready`).

export function AppRoutes(): JSX.Element {
  const router = createBrowserRouter([
    {
      element: <AppContent />,
      children: routes,
    },
  ]);

  return <RouterProvider router={router} />;
}
